import { useState, useEffect, useCallback } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';
import {
  CloudUpload,
  Clock,
  Users,
  Coffee,
  HardDrive,
  Database,
  Camera,
  UserCheck,
  Wifi,
  WifiOff,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import * as dbOps from '../db/kioskDbOperations';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(date) {
  if (!date) return 'Never';
  const now = Date.now();
  const diffMs = now - new Date(date).getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
  if (diffHr < 24) return `${diffHr} hour${diffHr !== 1 ? 's' : ''} ago`;
  return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
}

function getDayLabel(dateStr) {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString([], { weekday: 'short' });
}

// ---------------------------------------------------------------------------
// StatCard
// ---------------------------------------------------------------------------

// eslint-disable-next-line no-unused-vars
function StatCard({ icon: Icon, label, value, color = 'text-slate-700', loading }) {
  return (
    <Card className="py-4">
      <CardContent className="px-4 flex items-center gap-4">
        <div className={`p-2.5 rounded-lg bg-slate-100 ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <p className="text-sm text-slate-500 truncate">{label}</p>
          {loading ? (
            <Skeleton className="h-7 w-16 mt-0.5" />
          ) : (
            <p className="text-2xl font-bold text-slate-800 tabular-nums">{value}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// WeekChart - simple horizontal bars
// ---------------------------------------------------------------------------

function WeekChart({ data, loading }) {
  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-slate-400">
        No activity this week
      </div>
    );
  }

  const maxClockIns = Math.max(...data.map((d) => d.clockIns), 1);

  return (
    <div className="space-y-2.5">
      {data.map((day) => (
        <div key={day.day} className="flex items-center gap-3">
          <span className="text-xs font-medium text-slate-500 w-8 text-right flex-none">
            {getDayLabel(day.day)}
          </span>
          <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden relative">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(day.clockIns / maxClockIns) * 100}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="h-full bg-slate-700 rounded-full min-w-[2px]"
            />
          </div>
          <span className="text-sm font-semibold text-slate-700 w-8 text-right tabular-nums flex-none">
            {day.clockIns}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// KioskAdminDashboard
// ---------------------------------------------------------------------------

export default function KioskAdminDashboard({ syncEngine }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pendingCount: 0,
    todayClockIns: 0,
    onSiteCount: 0,
    todayBreaks: 0,
    weekData: [],
    storage: null,
    eventCount: 0,
    selfieCount: 0,
    employeeCount: 0,
  });

  const loadData = useCallback(async () => {
    try {
      const [
        todayEvents,
        onSiteUsers,
        weekSummary,
        storageEstimate,
        totalEvents,
        selfieCount,
        employeeCount,
        pendingCount,
      ] = await Promise.all([
        dbOps.getTodayEvents(),
        dbOps.getOnSiteUsers(),
        dbOps.getWeekSummary(),
        dbOps.getStorageEstimate(),
        dbOps.getTotalEventCount(),
        dbOps.getSelfieCount(),
        dbOps.getEmployeeCount(),
        dbOps.getPendingCount(),
      ]);

      const todayClockIns = todayEvents.filter((e) => e.action === 'clock_in').length;
      const todayBreaks = todayEvents.filter((e) => e.action === 'break_start').length;

      setStats({
        pendingCount,
        todayClockIns,
        onSiteCount: onSiteUsers.length,
        todayBreaks,
        weekData: weekSummary,
        storage: storageEstimate,
        eventCount: totalEvents,
        selfieCount,
        employeeCount,
      });
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Kiosk overview and status at a glance</p>
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* Sync Status Card                                                  */}
      {/* ----------------------------------------------------------------- */}
      <Card className="py-4">
        <CardHeader className="px-4 pb-0">
          <CardTitle className="text-base flex items-center gap-2">
            <CloudUpload className="w-4 h-4 text-slate-500" />
            Sync Status
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pt-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* Online / Offline */}
            {syncEngine.isOnline ? (
              <Badge variant="success" className="gap-1.5">
                <Wifi className="w-3 h-3" />
                Online
              </Badge>
            ) : (
              <Badge variant="destructive" className="gap-1.5">
                <WifiOff className="w-3 h-3" />
                Offline
              </Badge>
            )}

            {/* Pending count */}
            {stats.pendingCount > 0 ? (
              <Badge variant="warning" className="gap-1.5">
                <AlertCircle className="w-3 h-3" />
                {stats.pendingCount} pending
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1.5 text-slate-600">
                <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                All synced
              </Badge>
            )}

            {/* Auto-sync */}
            <Badge variant="outline" className="gap-1.5 text-slate-600">
              Auto-sync {syncEngine.autoSync ? 'on' : 'off'}
            </Badge>
          </div>

          {/* Last sync */}
          <p className="text-xs text-slate-400 mt-3">
            Last synced: {formatRelativeTime(syncEngine.lastSyncTime)}
          </p>
        </CardContent>
      </Card>

      {/* ----------------------------------------------------------------- */}
      {/* Today's Summary                                                   */}
      {/* ----------------------------------------------------------------- */}
      <div>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
          Today
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatCard
            icon={Clock}
            label="Clock-ins"
            value={stats.todayClockIns}
            color="text-blue-600"
            loading={loading}
          />
          <StatCard
            icon={Users}
            label="Currently on site"
            value={stats.onSiteCount}
            color="text-emerald-600"
            loading={loading}
          />
          <StatCard
            icon={Coffee}
            label="Breaks taken"
            value={stats.todayBreaks}
            color="text-amber-600"
            loading={loading}
          />
        </div>
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* This Week                                                         */}
      {/* ----------------------------------------------------------------- */}
      <Card className="py-4">
        <CardHeader className="px-4 pb-0">
          <CardTitle className="text-base">This Week</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pt-3">
          <WeekChart data={stats.weekData} loading={loading} />
        </CardContent>
      </Card>

      {/* ----------------------------------------------------------------- */}
      {/* Storage                                                           */}
      {/* ----------------------------------------------------------------- */}
      <div>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
          Storage
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard
            icon={HardDrive}
            label="Used"
            value={stats.storage?.usedMB != null ? `${stats.storage.usedMB} MB` : '--'}
            color="text-slate-600"
            loading={loading}
          />
          <StatCard
            icon={Database}
            label="Events"
            value={stats.eventCount}
            color="text-slate-600"
            loading={loading}
          />
          <StatCard
            icon={Camera}
            label="Selfies"
            value={stats.selfieCount}
            color="text-slate-600"
            loading={loading}
          />
          <StatCard
            icon={UserCheck}
            label="Employees"
            value={stats.employeeCount}
            color="text-slate-600"
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
}
